import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { apiCall } from "services/api";

import { UserFormData } from "store/auth/types";

import { toFormData } from "utils/files";

export const defaultUsersFilters = {
  "filter[trashed]": "with",
};

export type UsersFilters = Record<string, string | number>;

export const getUsers = createAsyncThunk(
  "/auth/users",

  async (filters: UsersFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/auth/users", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProfile = createAsyncThunk(
  "/auth/profile/:id",
  async (
    { userId, values }: { userId: number; values: Partial<UserFormData> },
    { rejectWithValue }
  ) => {
    try {
      const formdata = toFormData(values);

      const response = await apiCall().post(`/auth/user/${userId}`, formdata);
      toast.success("Profile updated");
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProfile = createAsyncThunk(
  "/auth/users/:id",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/auth/users/${id}`);

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deactivateUser = createAsyncThunk(
  "/auth/users/deactivate",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().delete(`/auth/users/${id}/deactivate`);
      toast.success(response.data.status);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const activateUser = createAsyncThunk(
  "/auth/users/activate",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/auth/users/${id}/activate`);
      toast.success(response.data.status);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const enableUser = createAsyncThunk(
  "/auth/users/enable",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(`/auth/users/${id}/enable`);
      toast.success(response.data.status);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getInvitableUsersToNewMeeting = createAsyncThunk(
  "/meetings/invitable-users",
  async (filters: UsersFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/users/invitable-to-meetings`, {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getInvitableUserToMeeting = createAsyncThunk(
  "/meetings/invitable-user",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        `/users/invitable-to-meetings/${userId}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getInvitableUsersToTask = createAsyncThunk(
  "/tasks/invitable-users",
  async (filters: UsersFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/users/invitable-to-tasks`, {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getInvitableUserToTask = createAsyncThunk(
  "/tasks/invitable-user",
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(
        `/users/invitable-to-tasks/${userId}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getInvitableUsersToVoting = createAsyncThunk(
  "/votings/invitable-users",
  async (filters: UsersFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/users/invitable-to-votings`, {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
